/**
 * Honeypot field + minimum-time measurement for every storefront submission that reaches our
 * service (task TL4). Cheap, invisible bot protection: a real parent never sees or fills the
 * trap.
 *
 * THE TWO RULES ARE NOT TREATED ALIKE (rework, 2026-10-13). The trap is ENFORCED everywhere: a
 * filled trap means the request is not sent. The time rule REFUSES on the support contact form
 * only; on everything that reaches our service it is measured and sent, never used to refuse,
 * because a wrong guess there silently discards a real customer's submission while showing them
 * success. See MIN_SUBMIT_MS.
 *
 * WHAT THIS IS NOT: it is not a CAPTCHA and it does not stop a targeted attacker. Anyone who
 * reads this file can leave the trap empty and wait two seconds. It raises the cost of the
 * indiscriminate, form-filling bot traffic that arrives with ads, nothing more. The service side
 * (task S75) is what can act on the signal.
 *
 * WHEN THE TRAP TRIPS the client does not send the request, shows the normal success state, and
 * stops. A bot gets the same page a parent gets and learns nothing from the difference.
 *
 * Everything is exported on window.BotTrap, and no function reads a global clock or document:
 * `now` and the root element arrive as arguments, which is what lets
 * scripts/verify-bot-trap.js drive it against a fixture.
 */
(function () {
  'use strict';

  /**
   * CHOSEN, not measured: two seconds is the usual honeypot floor, and it is comfortably below
   * any real completion of these forms — the shortest one asks for a child's name, a birth date
   * and a photo.
   *
   * WHERE IT IS ENFORCED, AND WHERE IT IS ONLY A SIGNAL (rework, 2026-10-13). A time rule can
   * only ever guess, and when it guesses wrong on a real customer the submission vanishes while
   * they are shown success — they believe their photo uploaded, or their book was approved, and
   * nothing was recorded. That failure is silent on both sides, so it is not a trade worth
   * making on anything that reaches our service.
   *
   * So the time rule REFUSES only on the support contact form, which is a native Shopify POST
   * that touches nothing of ours and where the worst case is a lost spam message. Everywhere
   * else — the photo modal's create/upload/confirm/handoff and both proof pages'
   * approve/reject — the elapsed time is measured and SENT, never used to refuse. The trap
   * field stays enforced everywhere, because a real customer cannot fill a field they cannot
   * see or tab to.
   */
  const MIN_SUBMIT_MS = 2000;

  /**
   * The trap's name, kept here only so it can be referred to; the markup in
   * snippets/bot-trap-field.liquid is what actually renders it, and the module finds the field
   * through TRAP_SELECTOR rather than by name.
   *
   * IT IS DELIBERATELY NEUTRAL (rework 2, 2026-10-13). Browser autofill classifies a field from
   * the tokens in its name, id and label — company, organization, business, website, url, name,
   * email, phone, tel, address, street, city, zip, postal, country. This was `company_website`
   * with the label "Company website", which is the organization class; Chrome fills
   * organization- and address-class fields even when `autocomplete="off"` is set. A parent
   * accepting an autofill suggestion elsewhere on the form could therefore have had the trap
   * filled for them, and their submission dropped without either side knowing.
   *
   * A honeypot may cost a bot. It may never cost a customer, and a name that invites autofill
   * is a name that eventually will.
   */
  const TRAP_FIELD_NAME = 'fs_hp_field';
  const TRAP_SELECTOR = '[data-bot-trap]';

  /**
   * Wire names for the service (task S75).
   *
   * `hp` is the trap value, normally the empty string.
   *
   * The elapsed time is `bt`, NOT `t` as the TL4 brief proposed. `t` is already the App Proxy
   * auth token on six routes — sheet-proof and interior-proof both read `@Query('t') token` —
   * so sending `t=<milliseconds>` would overwrite the token and break approve/reject. Measured,
   * not assumed; see the run report.
   *
   * Both travel as QUERY parameters, never in the JSON body. The service's global ValidationPipe
   * runs `whitelist: true, forbidNonWhitelisted: true`, so an unknown BODY field is a 400 —
   * measured against the real DTO, which answered "property hp should not exist". Query
   * parameters are not bound to a DTO on these routes and are already inside Shopify's App Proxy
   * signature, so they ride along safely and are ignored until S75 reads them.
   */
  const TRAP_PARAM = 'hp';
  const ELAPSED_PARAM = 'bt';

  const OPENED_AT = '__botTrapOpenedAt';

  /** Records when a form or modal became available to fill. */
  function markOpened(root, now) {
    if (!root) {
      return;
    }

    root[OPENED_AT] = now;
  }

  function openedAt(root) {
    return root && typeof root[OPENED_AT] === 'number' ? root[OPENED_AT] : null;
  }

  function elapsedSince(root, now) {
    const started = openedAt(root);
    return started === null ? null : now - started;
  }

  /** The trap's current value, trimmed. A bot that fills it with whitespace is still a bot. */
  function readTrap(root) {
    if (!root || typeof root.querySelector !== 'function') {
      return '';
    }

    const field = root.querySelector(TRAP_SELECTOR);

    if (!field || typeof field.value !== 'string') {
      return '';
    }

    return field.value.trim();
  }

  /**
   * The single decision both rules go through, so a caller cannot honour one and forget the
   * other. Returns what happened as well as whether to ignore, because the caller still has to
   * send `hp` and the elapsed time on a submission it does accept.
   *
   * `options.enforceTime` must be passed explicitly to make the time rule REFUSE. The default
   * is signal-only, and that direction is deliberate: a call site that forgets the option fails
   * towards letting a real customer through, not towards silently dropping them. Only
   * assets/bot-trap-form.js (the support contact form) opts in — see MIN_SUBMIT_MS.
   */
  function evaluate(root, now, options) {
    const opts = options || {};
    const hp = readTrap(root);
    const elapsed = elapsedSince(root, now);

    if (hp !== '') {
      return { ignore: true, reason: 'trap', hp, elapsed };
    }

    if (opts.enforceTime === true && elapsed !== null && elapsed < MIN_SUBMIT_MS) {
      return { ignore: true, reason: 'too-fast', hp, elapsed };
    }

    return { ignore: false, reason: null, hp, elapsed };
  }

  /**
   * Adds the two signals to a URL's query string, preserving any query it already carries —
   * the proof pages' URLs always arrive holding `?t=<token>`.
   */
  function appendSignals(url, verdict) {
    const separator = url.indexOf('?') === -1 ? '?' : '&';
    const elapsed = verdict && typeof verdict.elapsed === 'number' ? verdict.elapsed : 0;
    const hp = verdict && typeof verdict.hp === 'string' ? verdict.hp : '';

    return (
      url +
      separator +
      TRAP_PARAM +
      '=' +
      encodeURIComponent(hp) +
      '&' +
      ELAPSED_PARAM +
      '=' +
      encodeURIComponent(String(elapsed))
    );
  }

  window.BotTrap = {
    MIN_SUBMIT_MS,
    TRAP_FIELD_NAME,
    TRAP_SELECTOR,
    TRAP_PARAM,
    ELAPSED_PARAM,
    markOpened,
    openedAt,
    elapsedSince,
    readTrap,
    evaluate,
    appendSignals
  };
})();
